import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Empty } from './kit';

interface EmptyStateAction {
  label: string;
  href?: string;
  onClick?: () => void;
}

interface EmptyStateProps {
  /** Line-art mark from `lib/marks`. Omit for transient states (a filter miss). */
  mark?: string;
  eyebrow?: React.ReactNode;
  title: string;
  description: string;
  action?: EmptyStateAction;
  /** Monospace footnote: where the rows will actually come from. */
  hint?: React.ReactNode;
  compact?: boolean;
}

/**
 * Framed empty state with a single declarative action.
 *
 * A thin wrapper over kit's <Empty> — the design lives there, so a screen using
 * this and a screen using <Empty> directly can never drift apart.
 */
const EmptyState: React.FC<EmptyStateProps> = ({
  mark,
  eyebrow,
  title,
  description,
  action,
  hint,
  compact,
}) => {
  const button = action && (
    action.href ? (
      <Link to={action.href}>
        <motion.span
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.99 }}
          className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-medium text-white transition-transform"
        >
          {action.label}
        </motion.span>
      </Link>
    ) : (
      <motion.button
        onClick={action.onClick}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.99 }}
        className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-medium text-white transition-transform"
      >
        {action.label}
      </motion.button>
    )
  );

  return (
    <Empty
      framed
      mark={mark}
      eyebrow={eyebrow}
      title={title}
      description={description}
      action={button}
      hint={hint}
      compact={compact}
    />
  );
};

export default EmptyState;
